import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { AlarmClock, ArrowLeft, Eye, Flag, Mail, MailOpen, PanelRightOpen, Reply, ShieldAlert, Sparkles, Tag, X } from 'lucide-react';
import type { Mailbox, ThreadActivity } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { useThread, useUpdateThread, type ThreadDetail } from '../../hooks/useThreads';
import { useSettings } from '../../hooks/useMe';
import { useCategoryMap, useSetThreadCategory } from '../../hooks/useCategories';
import { displayName, fullDate, relativeTime } from '../../lib/format';
import { Avatar } from '../ui/Avatar';
import { ErrorState, Skeleton, SkeletonLines } from '../ui/Feedback';
import { useToast } from '../ui/Toast';
import { spring } from '../ui/motion';
import { MessageItem } from './MessageItem';
import { AssignButton, ReplyingBanner, TeamNotes } from './TeamPanel';
import { AddToProjectButton, ProjectSuggestion, ThreadProjectChips } from '../projects/ThreadProjects';
import { AnalysisCard } from './AnalysisCard';
import { Composer, type ComposerHandle } from './Composer';
import { CategoryChip, CategoryPicker } from './Category';
import { isEditableDraft } from '../../hooks/useDraftEditor';

export interface ThreadViewHandle {
  reply: () => void;
  toggleFlag: () => void;
  toggleUnread: () => void;
}

interface Props {
  threadId: string;
  mailboxes: Map<string, Mailbox>;
  onBack?: () => void;
  /** Details (context) sheet state; `details` is rendered inside the panel when open (desktop). */
  detailsOpen: boolean;
  onToggleDetails: () => void;
  details: ReactNode;
  onLoaded?: (d: ThreadDetail) => void;
}

/** Icon action in the reading-pane toolbar. */
function Tool({ label, icon, active, onClick, disabled, tone }: { label: string; icon: ReactNode; active?: boolean; onClick: () => void; disabled?: boolean; tone?: 'warn' }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      data-tip={label}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'tip flex h-9 w-9 items-center justify-center rounded-xl transition-[background-color,transform] hover:scale-105 active:scale-95 disabled:opacity-50',
        active ? (tone === 'warn' ? 'bg-warn-soft text-warn-ink' : 'bg-primary-soft text-primary-ink') : 'text-muted hover:bg-raised hover:text-ink',
      )}
    >
      {icon}
    </button>
  );
}

/** "Remind me if no reply": when it's due, or the date it will come back. Click to cancel. */
function FollowUpChip({ threadId, at, due }: { threadId: string; at: string; due: boolean }) {
  const qc = useQueryClient();
  const clear = useMutation({
    mutationFn: () => api('POST /api/threads/:id/follow-up', { params: { id: threadId }, body: { days: null } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.thread(threadId) });
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
    },
  });
  const when = new Date(at).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return (
    <button
      type="button"
      onClick={() => clear.mutate()}
      title={due ? 'No reply yet — click to dismiss the reminder' : `Comes back ${fullDate(at)} if nobody replies — click to cancel`}
      className={clsx('mp-pill group', due ? 'mp-pill-salmon' : 'mp-pill-lavender')}
    >
      <AlarmClock className="h-3 w-3" aria-hidden />
      {due ? 'No reply yet' : `Reminder ${when}`}
      <X className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
    </button>
  );
}

const firstName = (name: string | null, email: string | null) => (name || email || 'Someone').split(' ')[0];

/** Organizations: who on the team has opened this conversation and who replied (hover for times). */
function TeamActivity({ activity }: { activity: ThreadActivity }) {
  const { readBy, repliedBy } = activity;
  if (!readBy.length && !repliedBy.length) return null;
  const replyName = (r: ThreadActivity['repliedBy'][number]) => (r.kind === 'manual' ? firstName(r.name, r.email) : 'MailPilot (automatic)');
  const lastReply = repliedBy[repliedBy.length - 1];
  const title = [
    ...readBy.map((r) => `Read by ${r.name || r.email} · ${fullDate(r.at)}`),
    ...repliedBy.map((r) => `Replied by ${r.kind === 'manual' ? r.name || r.email || 'someone' : 'MailPilot (automatic)'} · ${fullDate(r.at)}`),
  ].join('\n');
  return (
    <span className="flex items-center gap-2 text-subtle" title={title}>
      {readBy.length > 0 && (
        <span className="inline-flex items-center gap-1">
          <Eye className="h-3.5 w-3.5" aria-hidden />
          Read by {readBy.slice(0, 3).map((r) => firstName(r.name, r.email)).join(', ')}
          {readBy.length > 3 ? ` +${readBy.length - 3}` : ''}
        </span>
      )}
      {lastReply && (
        <span className="inline-flex items-center gap-1">
          <Reply className="h-3.5 w-3.5" aria-hidden />
          Replied by {replyName(lastReply)} · {relativeTime(lastReply.at)}
          {repliedBy.length > 1 ? ` (${repliedBy.length} replies)` : ''}
        </span>
      )}
    </span>
  );
}

export const ThreadView = forwardRef<ThreadViewHandle, Props>(function ThreadView({ threadId, mailboxes, onBack, detailsOpen, onToggleDetails, details, onLoaded }, ref) {
  const q = useThread(threadId);
  const settings = useSettings();
  const update = useUpdateThread();
  const categories = useCategoryMap();
  const setCategory = useSetThreadCategory();
  const qc = useQueryClient();
  const toast = useToast();
  const composer = useRef<ComposerHandle>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const markedRead = useRef<string | null>(null);

  const analyse = useMutation({
    mutationFn: () => api('POST /api/threads/:id/analyse', { params: { id: threadId }, body: { force: true } }),
    onSuccess: (r) => qc.setQueryData<ThreadDetail>(qk.thread(threadId), (prev) => (prev ? { ...prev, analysis: r.analysis } : prev)),
    onError: (e) => toast.error(e),
  });

  const d = q.data;
  useEffect(() => {
    if (d) onLoaded?.(d);
  }, [d, onLoaded]);

  // mark read when opened (this never acknowledges reminders)
  useEffect(() => {
    if (d?.thread.unread && markedRead.current !== threadId) {
      markedRead.current = threadId;
      update.mutate({ id: threadId, unread: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.thread.unread, threadId]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
    setHighlight(null);
  }, [threadId]);

  const showEvidence = useCallback((messageId: string) => {
    setHighlight(null);
    requestAnimationFrame(() => {
      setHighlight(messageId);
      document.getElementById(`msg-${messageId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  useImperativeHandle(ref, () => ({
    reply: () => composer.current?.focus(),
    toggleFlag: () => d && update.mutate({ id: threadId, flagged: !d.thread.flagged }),
    toggleUnread: () => d && update.mutate({ id: threadId, unread: !d.thread.unread }),
  }));

  if (q.isPending) {
    return (
      <div className="space-y-4 p-6" aria-busy="true" aria-label="Loading conversation">
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
        <div className="rounded-card bg-raised p-5"><SkeletonLines lines={4} /></div>
        <div className="rounded-card bg-raised p-5"><SkeletonLines lines={6} /></div>
      </div>
    );
  }
  if (q.error || !d) return <ErrorState error={q.error} onRetry={() => void q.refetch()} title="Couldn't open this conversation" />;

  const { thread, messages, analysis, drafts, injectionWarnings, activity, team } = d;
  const mb = mailboxes.get(thread.mailboxId);
  const lastIdx = messages.length - 1;
  const cat = thread.category ? categories.get(thread.category) : undefined;
  const people = thread.participants.slice(0, 4);

  return (
    <motion.div key={threadId} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={spring} className="flex h-full min-h-0 flex-col">
      <header className="relative z-10 flex flex-wrap items-start gap-x-3 gap-y-2 border-b border-hairline px-4 py-3 md:flex-nowrap md:px-5">
        {mb && <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] rounded-t-panel" style={{ background: `linear-gradient(90deg, ${mb.color}, transparent 60%)` }} />}
        {onBack && (
          <button type="button" aria-label="Back to list" onClick={onBack} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-muted hover:bg-raised hover:text-ink">
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="line-clamp-2 text-[1.0625rem] font-semibold leading-snug text-ink md:truncate md:text-lg">{thread.subject || '(no subject)'}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-muted">
            {mb && (
              <span className="mp-pill mp-pill-neutral" title={mb.email}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: mb.color }} aria-hidden />
                {mb.displayName}
              </span>
            )}
            <CategoryPicker
              categories={[...categories.values()]}
              value={thread.category}
              onChange={(key) => setCategory.mutate({ id: threadId, category: key })}
              trigger={(t) => (
                <button type="button" {...t} aria-label={cat ? `Category: ${cat.name}. Change category` : 'Choose a category'} className="group rounded-full transition-transform hover:scale-[1.04]">
                  {cat ? (
                    <CategoryChip category={cat} source={thread.categorySource} className="!py-0.5 !text-[0.6875rem] ring-1 ring-transparent group-hover:ring-line" />
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-line px-2 py-0.5 text-[0.6875rem] font-medium text-muted group-hover:border-primary/50 group-hover:text-primary-ink">
                      <Tag className="h-3 w-3" /> Category
                    </span>
                  )}
                </button>
              )}
            />
            {thread.awaitingReplyFrom === 'me' && <span className="mp-pill mp-pill-yellow">Needs your reply</span>}
            <ThreadProjectChips thread={thread} />
            {thread.followUp && <FollowUpChip threadId={threadId} at={thread.followUp.at} due={thread.followUp.due} />}
            <span className="flex items-center gap-1.5">
              <span className="flex -space-x-1">
                {people.map((p) => (
                  <Avatar key={p.email} name={p.name} email={p.email} size={20} ring="rgb(var(--surface))" />
                ))}
              </span>
              <span className="max-w-[16rem] truncate" title={thread.participants.map((p) => displayName(p)).join(', ')}>
                {people.map((p) => displayName(p).split(' ')[0]).join(', ')}
                {thread.participants.length > 4 ? ` +${thread.participants.length - 4}` : ''}
              </span>
              <span className="hidden text-subtle sm:inline">· {thread.messageCount} message{thread.messageCount === 1 ? '' : 's'}</span>
            </span>
            {activity && <TeamActivity activity={activity} />}
          </div>
        </div>
        <div className="flex basis-full shrink-0 items-center justify-end gap-1 md:basis-auto">
          <button
            type="button"
            onClick={() => composer.current?.focus()}
            data-tip="Reply (r)"
            className="tip mr-1 hidden h-9 items-center gap-1.5 rounded-xl bg-primary px-3.5 text-[0.8125rem] font-semibold text-primary-fg shadow-primary transition-colors hover:bg-primary-hover sm:inline-flex"
          >
            <Reply className="h-4 w-4" /> Reply
          </button>
          <AddToProjectButton thread={thread} />
          {team && <AssignButton thread={thread} />}
          <div className="flex items-center gap-0.5 rounded-xl bg-sunken/70 p-0.5">
            <Tool label={thread.flagged ? 'Unflag (s)' : 'Flag (s)'} active={thread.flagged} tone="warn" icon={<Flag className={clsx('h-[18px] w-[18px]', thread.flagged && 'fill-current')} />} onClick={() => update.mutate({ id: threadId, flagged: !thread.flagged })} />
            <Tool label={thread.unread ? 'Mark as read (u)' : 'Mark as unread (u)'} icon={thread.unread ? <MailOpen className="h-[18px] w-[18px]" /> : <Mail className="h-[18px] w-[18px]" />} onClick={() => update.mutate({ id: threadId, unread: !thread.unread })} />
            <Tool label="Analyse with AI" icon={<Sparkles className={clsx('h-[18px] w-[18px]', analyse.isPending && 'animate-pulse')} />} disabled={analyse.isPending} onClick={() => analyse.mutate()} />
            <Tool label={detailsOpen ? 'Hide details (i)' : 'Details (i)'} active={detailsOpen} icon={<PanelRightOpen className="h-[18px] w-[18px]" />} onClick={onToggleDetails} />
          </div>
        </div>
      </header>

      <div className="relative min-h-0 flex-1">
        <div ref={scroller} className="h-full overflow-y-auto scroll-thin">
          <div className="mx-auto max-w-3xl space-y-4 px-3 py-4 md:px-6 md:py-6">
            {injectionWarnings.length > 0 && (
              <div role="alert" className="flex items-start gap-3 rounded-2xl border border-danger/30 bg-danger-soft px-4 py-3 text-danger-ink">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="text-[0.8125rem]">
                  <p className="font-semibold">This email contains text that looks like instructions to an AI. MailPilot ignores instructions inside emails.</p>
                  <ul className="mt-1 list-disc pl-4 text-xs">
                    {injectionWarnings.slice(0, 3).map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              </div>
            )}
            <ProjectSuggestion thread={thread} />
            <AnalysisCard threadId={threadId} analysis={analyse.data?.analysis ?? analysis} onEvidence={showEvidence} />
            {messages.map((m, i) => (
              <MessageItem key={m.id} m={m} isOnlyMessage={messages.length === 1} defaultOpen={i === lastIdx || m.unread || i >= lastIdx - 1} highlighted={highlight === m.id} blockRemoteImages={settings?.blockRemoteImages ?? true} />
            ))}
            <div className="pt-2">
              {team && (
                <div className="mb-3 space-y-3">
                  <ReplyingBanner replying={team.replying} />
                  <TeamNotes threadId={threadId} notes={team.notes} />
                </div>
              )}
              <Composer key={threadId} ref={composer} mode="reply" thread={thread} messages={messages} drafts={drafts} onEvidence={showEvidence} startCollapsed={!drafts.some(isEditableDraft)} />
            </div>
          </div>
        </div>
        {/* Details sheet: slides in over the reading pane on desktop. */}
        <AnimatePresence>
          {detailsOpen && details !== null && (
            <motion.aside
              key="details"
              initial={{ x: 24, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 24, opacity: 0 }}
              transition={spring}
              aria-label="Conversation details"
              className="absolute inset-y-0 right-0 z-20 flex w-[min(360px,100%)] flex-col border-l border-hairline bg-surface/95 shadow-float backdrop-blur-xl"
            >
              <div className="flex items-center justify-between border-b border-hairline px-4 py-2.5">
                <h2 className="text-[0.8125rem] font-semibold text-ink">Details</h2>
                <button type="button" aria-label="Hide details" onClick={onToggleDetails} className="flex h-8 w-8 items-center justify-center rounded-lg text-muted hover:bg-raised hover:text-ink">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto scroll-thin">{details ?? <p className="p-5 text-xs text-muted">Loading details…</p>}</div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
});
